// src/components/compliance/OverviewTab.tsx
//
// The Compliance Overview dashboard. All numbers come from the
// backend coverage summary (`/api/compliance/coverage/summary`) — we
// never invent metrics client-side. The subject-type breakdowns
// (contacts / providers / departments) come from the same endpoint
// via `bySubjectType`.

import { useMemo } from 'react';
import {
  useGetComplianceCoverageSummaryQuery,
  useGetComplianceCoverageByRequirementQuery,
} from '../../api/complianceApi';
import LoadingSpinner from '../common/LoadingSpinner';
import ErrorState from '../common/ErrorState';
import { EnforcementModeChip } from './Chips';
import { messageFromComplianceError } from './complianceDisplay';

interface OverviewTabProps {
  workspaceId: string;
  onNavigateTab: (
    tab: 'my' | 'documents' | 'expiring' | 'approvals' | 'reviews' | 'requirements' | 'categories' | 'overview',
  ) => void;
}

export default function OverviewTab({ workspaceId, onNavigateTab }: OverviewTabProps) {
  const {
    data: summary,
    isLoading: sumLoading,
    error: sumError,
  } = useGetComplianceCoverageSummaryQuery(undefined, { skip: !workspaceId });

  const {
    data: reqCoverage,
    isLoading: reqLoading,
    error: reqError,
  } = useGetComplianceCoverageByRequirementQuery(undefined, { skip: !workspaceId });

  const overallScore = useMemo(() => {
    if (!summary) return null;
    if (summary.totals.total === 0) return null;
    // ok / total, expressed 0-100.
    return Math.round((summary.totals.ok / summary.totals.total) * 100);
  }, [summary]);

  if (sumLoading) return <LoadingSpinner />;
  if (sumError) {
    return (
      <ErrorState
        title="Could not load compliance overview"
        message={messageFromComplianceError(sumError)}
      />
    );
  }
  if (!summary) return null;

  return (
    <div className="space-y-6" data-testid="compliance-overview">
      {/* Row 1 — top-line KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard
          label="Overall compliance"
          testId="compliance-kpi-score"
          value={overallScore === null ? '—' : `${overallScore}%`}
          hint={overallScore === null ? 'No subjects with requirements yet' : `${summary.totals.ok}/${summary.totals.total} subjects`}
          tone={
            overallScore === null
              ? 'neutral'
              : overallScore >= 90
                ? 'ok'
                : overallScore >= 70
                  ? 'warning'
                  : 'blocked'
          }
        />
        <KpiCard
          label="Expiring ≤30 days"
          testId="compliance-kpi-expiring"
          value={String(summary.expiringSoonCount)}
          onClick={() => onNavigateTab('expiring')}
          tone={summary.expiringSoonCount > 0 ? 'warning' : 'ok'}
        />
        <KpiCard
          label="Expired"
          testId="compliance-kpi-expired"
          value={String(summary.overdueExpiredCount)}
          onClick={() => onNavigateTab('expiring')}
          tone={summary.overdueExpiredCount > 0 ? 'blocked' : 'ok'}
        />
        <KpiCard
          label="Hard-block requirements affecting people"
          testId="compliance-kpi-hardblock"
          value={String(summary.hardBlockedRequirementIds.length)}
          onClick={() => onNavigateTab('requirements')}
          tone={summary.hardBlockedRequirementIds.length > 0 ? 'blocked' : 'ok'}
        />
      </div>

      {/* Row 2 — status breakdowns per subject type */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <SubjectBreakdownCard
          title="Contacts"
          bucket={summary.bySubjectType.contact}
          testId="compliance-breakdown-contact"
        />
        <SubjectBreakdownCard
          title="Providers"
          bucket={summary.bySubjectType.provider_user}
          testId="compliance-breakdown-provider"
          extraFooter="Blocked providers cannot be assigned to work when a hard_block requirement is unsatisfied."
        />
        <SubjectBreakdownCard
          title="Departments"
          bucket={summary.bySubjectType.department_circle}
          testId="compliance-breakdown-department"
        />
      </div>

      {/* Row 3 — requirement-centric heat map (top offenders) */}
      <section className="rounded-lg border border-gray-200 bg-white" data-testid="compliance-requirement-heatmap">
        <header className="px-4 py-3 border-b border-gray-100 flex items-center justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">Requirement coverage</h2>
            <p className="text-xs text-gray-500">Live counts per requirement across your workspace.</p>
          </div>
        </header>
        {reqLoading ? (
          <div className="p-4"><LoadingSpinner /></div>
        ) : reqError ? (
          <div className="p-4">
            <ErrorState
              title="Could not load requirement coverage"
              message={messageFromComplianceError(reqError)}
            />
          </div>
        ) : !reqCoverage || reqCoverage.length === 0 ? (
          <div className="p-6 text-center text-sm text-gray-500">No active requirements yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-4 py-2">Requirement</th>
                  <th className="px-4 py-2">Enforcement</th>
                  <th className="px-4 py-2 text-right">Assignments</th>
                  <th className="px-4 py-2 text-right">Satisfied</th>
                  <th className="px-4 py-2 text-right">Expiring</th>
                  <th className="px-4 py-2 text-right">Expired</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {reqCoverage.map((row) => (
                  <tr key={row.requirementId} className="hover:bg-gray-50">
                    <td className="px-4 py-2 text-gray-900">{row.requirementName}</td>
                    <td className="px-4 py-2"><EnforcementModeChip mode={row.enforcement} /></td>
                    <td className="px-4 py-2 text-right text-gray-700">{row.totalAssignments}</td>
                    <td className="px-4 py-2 text-right text-emerald-700">{row.satisfiedDocuments}</td>
                    <td className="px-4 py-2 text-right text-amber-700">{row.expiringDocuments}</td>
                    <td className="px-4 py-2 text-right text-red-700">{row.expiredDocuments}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

type Tone = 'ok' | 'warning' | 'blocked' | 'neutral';

const TONE_RING: Record<Tone, string> = {
  ok: 'ring-emerald-200',
  warning: 'ring-amber-200',
  blocked: 'ring-red-200',
  neutral: 'ring-gray-200',
};

const TONE_TEXT: Record<Tone, string> = {
  ok: 'text-emerald-700',
  warning: 'text-amber-700',
  blocked: 'text-red-700',
  neutral: 'text-gray-800',
};

function KpiCard({
  label,
  value,
  hint,
  tone,
  onClick,
  testId,
}: {
  label: string;
  value: string;
  hint?: string;
  tone: Tone;
  onClick?: () => void;
  testId?: string;
}) {
  const clickable = !!onClick;
  const Element = clickable ? 'button' : 'div';
  return (
    <Element
      type={clickable ? 'button' : undefined}
      onClick={onClick}
      data-testid={testId}
      className={`rounded-lg bg-white ring-1 ${TONE_RING[tone]} p-4 text-left transition-colors ${
        clickable ? 'hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500' : ''
      }`}
    >
      <div className="text-xs uppercase tracking-wide text-gray-500">{label}</div>
      <div className={`mt-1 text-2xl font-semibold ${TONE_TEXT[tone]}`}>{value}</div>
      {hint && <div className="mt-1 text-xs text-gray-500">{hint}</div>}
    </Element>
  );
}

function SubjectBreakdownCard({
  title,
  bucket,
  testId,
  extraFooter,
}: {
  title: string;
  bucket: { ok: number; warning: number; blocked: number; total: number };
  testId?: string;
  extraFooter?: string;
}) {
  return (
    <section
      className="rounded-lg border border-gray-200 bg-white p-4"
      data-testid={testId}
    >
      <header className="flex items-baseline justify-between gap-3 mb-3">
        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
        <span className="text-xs text-gray-500">{bucket.total} total</span>
      </header>
      <dl className="grid grid-cols-3 gap-2">
        <Metric label="OK" value={bucket.ok} tone="ok" />
        <Metric label="Warning" value={bucket.warning} tone="warning" />
        <Metric label="Blocked" value={bucket.blocked} tone="blocked" />
      </dl>
      {extraFooter && <p className="mt-3 text-xs text-gray-500">{extraFooter}</p>}
    </section>
  );
}

function Metric({ label, value, tone }: { label: string; value: number; tone: Tone }) {
  return (
    <div>
      <dt className="text-xs text-gray-500">{label}</dt>
      <dd className={`text-lg font-semibold ${TONE_TEXT[tone]}`}>{value}</dd>
    </div>
  );
}
